import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { IS_WEB } from "@common/utils/mobileUtils"
import { syncTutorEventsStartedAt } from "@data/redux/reducers/data/tutorEventsPanel"
import {
    autoActivityVerb,
    ESCALATION_DELAY_MS,
    EVENT_TYPES,
    eventHash,
    getEffectiveCta,
    SAM_ELIGIBLE,
    sortEventsByPriority,
} from "./eventsCatalog"

const SILENCE_THRESHOLD_SECONDS = 10 * 60
const OUT_OF_APP_DEBOUNCE_MS = 3 * 1000
// A live session never runs this long, so a persisted startedAt older than
// this can only be a leftover from a previous app run — ignore it on hydrate.
const HYDRATION_MAX_AGE_MS = 6 * 60 * 60 * 1000

export const useTutorEventsQueue = ({ students }) => {
    const dispatch = useDispatch()
    const dismissedEventHashes = useSelector(
        (state) =>
            state.data?.tutorEventsPanel?.dismissedEventHashes ?? [],
        shallowEqual,
    )
    const persistedStartedAt = useSelector(
        (state) => state.data?.tutorEventsPanel?.startedAtByKey ?? null,
    )
    const samSentAtByKey = useSelector(
        (state) => state.data?.tutorEventsPanel?.samSentAtByKey ?? null,
        shallowEqual,
    )

    const liveModeSignals = useSelector(
        (state) => ({
            raisedHands: state.liveMode?.raisedHands ?? [],
            outOfAppIds: state.liveMode?.outOfAppIds ?? [],
            timesSinceLastCall: state.liveMode?.timesSinceLastCall ?? {},
            soundModeIds: state.liveMode?.soundModeIds ?? [],
            ongoingCheckpointIds: state.liveMode?.ongoingCheckpointIds ?? [],
            liveParticipants: state.liveMode?.liveParticipants ?? [],
            recentFinalizedActivities:
                state.liveMode?.recentFinalizedActivities ?? {},
            autoModeStartedActivities:
                state.liveMode?.autoModeStartedActivities ?? {},
        }),
        shallowEqual,
    )

    const dialogueWithStudentId = useMemo(() => {
        const studentIds = (students ?? []).map((s) => s?.id)
        // Only count a student who is BOTH in Dialogue and still connected —
        // a disconnected student left in soundModeIds must not make the tutor
        // look "busy" and mis-route another student's CTA to "Envoyer Sam à X".
        const present = new Set(liveModeSignals.liveParticipants)
        return (
            liveModeSignals.soundModeIds.find(
                (id) => studentIds.includes(id) && present.has(id),
            ) ?? null
        )
    }, [
        liveModeSignals.soundModeIds,
        liveModeSignals.liveParticipants,
        students,
    ])

    const studentsById = useMemo(() => {
        const map = {}
        ;(students ?? []).forEach((s) => {
            if (s?.id) {
                map[s.id] = {
                    id: s.id,
                    firstname: s.firstname ?? s.display_name ?? "?",
                    raw: s,
                }
            }
        })
        return map
    }, [students])

    const startedAtRef = useRef(new Map())
    const outOfAppDebounceRef = useRef(new Map())
    const resolvedRef = useRef(new Map())
    const hydratedRef = useRef(false)
    const pendingPersistRef = useRef({ additions: {}, removals: [] })
    const [tick, setTick] = useState(0)
    const forceUpdate = useCallback(() => setTick((t) => t + 1), [])

    // Hydrate startedAtRef from redux-persist during render (not in useEffect)
    // because the events useMemo below reads startedAtRef.current.get(key) on
    // the same render — an effect-based hydration would force a wasted first
    // render with fresh startedAt before the persisted values land. Ref writes
    // during render are safe per React docs (no re-render triggered).
    if (!hydratedRef.current && persistedStartedAt) {
        const hydrateNow = Date.now()
        Object.entries(persistedStartedAt).forEach(([key, value]) => {
            // Skip timers old enough to only come from a previous app run (a
            // refresh rehydrates within seconds) — reusing them would otherwise
            // show a huge elapsed time and trip the escalation instantly.
            if (hydrateNow - value > HYDRATION_MAX_AGE_MS) return
            if (!startedAtRef.current.has(key)) {
                startedAtRef.current.set(key, value)
            }
        })
        hydratedRef.current = true
    }

    useEffect(() => {
        const timer = setInterval(forceUpdate, 1000)
        return () => clearInterval(timer)
    }, [forceUpdate])

    useEffect(() => {
        const now = Date.now()
        const debounce = outOfAppDebounceRef.current
        const currentlyOut = new Set(liveModeSignals.outOfAppIds)

        currentlyOut.forEach((id) => {
            if (!debounce.has(id)) {
                debounce.set(id, now)
            }
        })
        Array.from(debounce.keys()).forEach((id) => {
            if (!currentlyOut.has(id)) {
                debounce.delete(id)
            }
        })
    }, [liveModeSignals.outOfAppIds])

    const events = useMemo(() => {
        const now = Date.now()
        const presentStudentIds = new Set(liveModeSignals.liveParticipants)
        const raw = []

        // Identity key for timing/dedup. EXERCISE_DONE is scoped by the
        // submitted activity (falling back to its finalize timestamp) so a
        // SECOND submission by the same student is a NEW event — fresh
        // startedAt + hash → it re-fires the snackbar and shows the right age,
        // instead of inheriting the first submission's identity.
        const keyForEvent = (e) => {
            if (e.type === EVENT_TYPES.EXERCISE_DONE) {
                return `${e.type}-${e.studentId}-${e.activityId ?? e.finalizedAt ?? ""}`
            }
            // An auto-mode choice is scoped by the chosen activity so each new
            // self-chosen activity is a fresh card.
            if (e.type === EVENT_TYPES.AUTO_ACTIVITY) {
                return `${e.type}-${e.studentId}-${e.activityId ?? ""}`
            }
            return `${e.type}-${e.studentId}`
        }

        Object.values(studentsById).forEach((student) => {
            if (!presentStudentIds.has(student.id)) return

            // While a student is in Dialogue the tutor is already focused on
            // them, so the "needs attention" signals are noise and stay
            // suppressed — but a submitted exercise is a discrete, actionable
            // event the tutor still wants to see, so it is NOT suppressed.
            const inDialogue = liveModeSignals.soundModeIds.includes(student.id)

            const studentEvents = []

            if (!inDialogue) {
                if (liveModeSignals.raisedHands.includes(student.id)) {
                    studentEvents.push({
                        type: EVENT_TYPES.HAND_RAISED,
                        studentId: student.id,
                    })
                }

                const silenceSec =
                    liveModeSignals.timesSinceLastCall[student.id] ?? 0
                if (silenceSec >= SILENCE_THRESHOLD_SECONDS) {
                    studentEvents.push({
                        type: EVENT_TYPES.SILENCE,
                        studentId: student.id,
                    })
                }

                const outStart = outOfAppDebounceRef.current.get(student.id)
                if (outStart && now - outStart >= OUT_OF_APP_DEBOUNCE_MS) {
                    studentEvents.push({
                        type: EVENT_TYPES.OUT_OF_APP,
                        studentId: student.id,
                    })
                }

                if (
                    liveModeSignals.ongoingCheckpointIds.includes(student.id)
                ) {
                    studentEvents.push({
                        type: EVENT_TYPES.SAM_ONGOING,
                        studentId: student.id,
                    })
                }
            }

            const finalized =
                liveModeSignals.recentFinalizedActivities[student.id]
            if (finalized) {
                studentEvents.push({
                    type: EVENT_TYPES.EXERCISE_DONE,
                    studentId: student.id,
                    activityId: finalized.activityId,
                    finalizedAt: finalized.finishedAt,
                })
            }

            // Like a submitted exercise, an autonomous next-activity choice is
            // a discrete actionable event — surfaced even while in Dialogue.
            const autoActivity =
                liveModeSignals.autoModeStartedActivities[student.id]
            if (autoActivity) {
                studentEvents.push({
                    type: EVENT_TYPES.AUTO_ACTIVITY,
                    studentId: student.id,
                    activityId: autoActivity.activityId,
                    verb: autoActivityVerb(autoActivity.evaluation),
                })
            }

            studentEvents.forEach((e) => {
                const key = keyForEvent(e)
                let startedAt = startedAtRef.current.get(key)
                if (!startedAt) {
                    startedAt = now
                    startedAtRef.current.set(key, startedAt)
                    pendingPersistRef.current.additions[key] = startedAt
                }
                raw.push({ ...e, key, startedAt, student })
            })
        })

        const activeKeys = new Set(raw.map((e) => e.key))
        Array.from(startedAtRef.current.keys()).forEach((key) => {
            if (!activeKeys.has(key)) {
                startedAtRef.current.delete(key)
                pendingPersistRef.current.removals.push(key)
                delete pendingPersistRef.current.additions[key]
            }
        })

        return raw
    }, [
        studentsById,
        liveModeSignals.raisedHands,
        liveModeSignals.timesSinceLastCall,
        liveModeSignals.ongoingCheckpointIds,
        liveModeSignals.liveParticipants,
        liveModeSignals.outOfAppIds,
        liveModeSignals.soundModeIds,
        liveModeSignals.recentFinalizedActivities,
        liveModeSignals.autoModeStartedActivities,
        tick,
    ])

    const dismissedSet = useMemo(
        () => new Set(dismissedEventHashes),
        [dismissedEventHashes],
    )

    const visibleEvents = useMemo(() => {
        const now = Date.now()
        const enrichedMap = new Map()

        events.forEach((e) => {
            const hash = eventHash(e)
            if (dismissedSet.has(hash)) return
            const isSamEligible = SAM_ELIGIBLE.has(e.type)
            // samSent reflects a TUTOR-initiated Sam send (persisted, keyed by
            // the event's stable key) — NOT a student-initiated checkpoint, so
            // the tutor's "Parler à X" CTA isn't hijacked into a passive state
            // just because the student opened Sam themselves.
            const samSentAt = isSamEligible
                ? samSentAtByKey?.[`${e.type}-${e.studentId}`] ?? null
                : null
            const samSent = samSentAt != null
            // Escalate 4 min after Sam was sent (so Sam gets its full window),
            // or 4 min after the event appeared when Sam was never sent.
            const escalated =
                isSamEligible &&
                now - (samSentAt ?? e.startedAt) >= ESCALATION_DELAY_MS
            const resolved = resolvedRef.current.get(hash)
            const enrichedEvent = {
                ...e,
                hash,
                samSent,
                escalated,
                isResolved: !!resolved,
                resolvedLabel: resolved?.label,
            }
            const cta = resolved
                ? { kind: "none", label: "", action: null }
                : getEffectiveCta(enrichedEvent, {
                      dialogueWithStudentId,
                      isWeb: IS_WEB,
                  })
            enrichedMap.set(hash, { ...enrichedEvent, cta })
        })

        resolvedRef.current.forEach((entry, hash) => {
            if (enrichedMap.has(hash) || dismissedSet.has(hash)) return
            enrichedMap.set(hash, {
                ...entry.event,
                hash,
                isResolved: true,
                resolvedLabel: entry.label,
                cta: { kind: "none", label: "", action: null },
            })
        })

        return sortEventsByPriority(Array.from(enrichedMap.values()))
    }, [events, dismissedSet, dialogueWithStudentId, samSentAtByKey, tick])

    // Flush pendingPersistRef populated during the events useMemo above.
    // Driven by [events] so it fires when the queue changes, not on every
    // render (1Hz tick would otherwise spam this effect needlessly).
    useEffect(() => {
        const { additions, removals } = pendingPersistRef.current
        const hasAdditions = Object.keys(additions).length > 0
        const hasRemovals = removals.length > 0
        if (!hasAdditions && !hasRemovals) return
        pendingPersistRef.current = { additions: {}, removals: [] }
        dispatch(
            syncTutorEventsStartedAt({
                additions: hasAdditions ? additions : undefined,
                removals: hasRemovals ? removals : undefined,
            }),
        )
    }, [events, dispatch])

    const resolvedTimeoutsRef = useRef(new Set())

    useEffect(
        () => () => {
            resolvedTimeoutsRef.current.forEach((t) => clearTimeout(t))
            resolvedTimeoutsRef.current.clear()
        },
        [],
    )

    const markResolved = useCallback(
        (event, label) => {
            if (!event?.hash) return
            resolvedRef.current.set(event.hash, {
                event,
                label,
                at: Date.now(),
            })
            forceUpdate()
            const timeoutId = setTimeout(() => {
                resolvedTimeoutsRef.current.delete(timeoutId)
                resolvedRef.current.delete(event.hash)
                forceUpdate()
            }, 3000)
            resolvedTimeoutsRef.current.add(timeoutId)
        },
        [forceUpdate],
    )

    return {
        events: visibleEvents,
        markResolved,
    }
}
